import { useMemo } from 'react';
import { CreateMetaplanRequestDto } from '@tiendanube/common';
import { useNavegate } from 'App/hooks';
import { toStatusType } from 'App/rtk';
import { useForm, useToastStatus } from 'commons/hooks';
import { CATALOG_ROUTES } from 'domains/Catalog/catalogRoutes';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { MetaplanPage, MetaplanForm } from './components';
import type { MetaplanFormValues } from './components/MetaplanForm/MetaplanForm';
import { getMetaplanDefault } from './constants';
import { validationSchema } from './validationSchema';
import {
  useCreateMetaplanMutation,
  useGetMetaplansQuery,
} from '../../subscriptionsApi';

export function NewMetaplanPage() {
  const t = useTranslationCatalog();
  const { goTo } = useNavegate();

  const [createMetaplan, { status: statusCreate }] =
    useCreateMetaplanMutation();

  const { data: metaplansData } = useGetMetaplansQuery();
  const existingMetaplans = useMemo(
    () => metaplansData?.items ?? [],
    [metaplansData?.items],
  );

  const handleCreate = (data: MetaplanFormValues) => {
    const validSubscriptionOptions = data.subscriptionOptions
      .filter((option) => {
        const frequencyNum = parseInt(option.frequencyParam || '0', 10);
        return frequencyNum >= 5;
      })
      .map((option) => ({
        frequencyType: option.frequencyType,
        frequencyParam: option.frequencyParam,
        discountPercentage: option.discountPercentage,
      }));

    const transformedData: CreateMetaplanRequestDto = {
      name: data.name,
      productIds: data.productIds,
      subscriptionOptions: validSubscriptionOptions,
      promotionCombinations: data.promotionCombinations,
      isSubscriptionOnly: data.isSubscriptionOnly,
    };

    createMetaplan(transformedData);
  };

  const initialValues: MetaplanFormValues = useMemo(
    () => getMetaplanDefault(),
    [],
  );

  const schema = useMemo(
    () => validationSchema(existingMetaplans),
    [existingMetaplans],
  );

  const {
    values,
    errors,
    isDirty,
    handleChange,
    setFieldValue,
    handleChangeSelect,
    handleOnSubmit,
    cleanErrors,
  } = useForm<MetaplanFormValues>({
    initialValues,
    validationSchema: schema,
    onSubmit: handleCreate,
  });

  const handleChangeProducts = (productIds: number[]) => {
    setFieldValue('productIds', productIds);
  };

  const excludedProductIds = useMemo(
    () =>
      existingMetaplans
        .flatMap((metaplan) => metaplan.productIds || [])
        .map((id) => String(id)),
    [existingMetaplans],
  );

  const saveStatus = toStatusType(statusCreate);
  const loadStatus = 'success' as const;

  useToastStatus({
    error: t('subscriptions.form.errorMessage'),
    success: t('subscriptions.form.successMessage'),
    status: saveStatus,
    onSuccess: () => goTo(CATALOG_ROUTES.metaplansList),
  });

  return (
    <MetaplanPage
      mode="add"
      isDirty={isDirty}
      saveStatus={saveStatus}
      loadStatus={loadStatus}
      onSave={handleOnSubmit}
    >
      {loadStatus === 'success' && (
        <MetaplanForm
          mode="add"
          values={values}
          errors={errors}
          metaplanId={undefined}
          onChange={handleChange}
          setFieldValue={setFieldValue}
          onChangeSelect={handleChangeSelect}
          cleanErrors={cleanErrors}
          onChangeProducts={handleChangeProducts}
          excludedProductIds={excludedProductIds}
        />
      )}
    </MetaplanPage>
  );
}
